import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, CheckCircle2, Eye, Send } from 'lucide-react';
import api from '../../../utils/authUtils';
import { Overlay, Modal, ModalTitle, ModalActions, Select, Message, Button } from './ui';
import { eventNameFor } from '../../../utils/eventName';

// Manual invite reminders, for one invitation or several at once. Nothing is
// sent before the admin has seen the exact email (the saved template, filled
// in for the real speaker - one at a time when several are selected). A
// manual reminder counts toward the automatic limit, but is never refused
// because of it: someone already at the limit just gets a warning here.
export const formatDay = (intl, value) => value
  ? intl.formatDate(new Date(value), { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const ReminderSendModal = ({ invites, maxAutomatic, onClose, onSent }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const [index, setIndex] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const current = invites[index];

  useEffect(() => {
    if (!current) return undefined;
    let live = true;
    setPreview(null);
    setPreviewError('');
    api.post('/api/cms/invite-reminders/preview', { invitationId: current.invitationId, highlight: false })
      .then(({ data }) => { if (live) setPreview(data); })
      .catch(err => {
        if (live) setPreviewError(typeof err.response?.data === 'string' && err.response.data
          ? err.response.data : t('inviteReminders.preview.error', 'Could not prepare the preview.'));
      });
    return () => { live = false; };
  }, [current?.invitationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const atLimit = invites.filter(i => i.reminderCount >= maxAutomatic);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/api/cms/invite-reminders/send', { invitationIds: invites.map(i => i.invitationId) });
      setResults(data.results || []);
      onSent?.();
    } catch (err) {
      setPreviewError(typeof err.response?.data === 'string' && err.response.data
        ? err.response.data : t('inviteReminders.send.error', 'The reminders could not be sent.'));
    } finally {
      setSending(false);
    }
  };
  const nameOf = id => invites.find(i => i.invitationId === id)?.name || id;

  if (results) {
    const sent = results.filter(r => r.sent);
    const failed = results.filter(r => !r.sent);
    return (
      <Overlay onClick={onClose}>
        <Modal onClick={e => e.stopPropagation()} style={{ maxWidth: '36rem' }}>
          <ModalTitle><CheckCircle2 size={18} color="#166534" />
            <FormattedMessage id="inviteReminders.send.doneTitle" defaultMessage="{count, plural, one {Reminder sent} other {# reminders sent}}" values={{ count: sent.length }} />
          </ModalTitle>
          {failed.length > 0 && (
            <Message error>
              <FormattedMessage id="inviteReminders.send.failed" defaultMessage="{count, plural, one {One reminder was} other {# reminders were}} not sent:" values={{ count: failed.length }} />
              <ul style={{ margin: '.4rem 0 0', paddingLeft: '1.1rem' }}>
                {failed.map(r => <li key={r.invitationId}><strong>{nameOf(r.invitationId)}</strong> — {r.error}</li>)}
              </ul>
            </Message>
          )}
          <ModalActions><Button onClick={onClose}><FormattedMessage id="inviteReminders.close" defaultMessage="Close" /></Button></ModalActions>
        </Modal>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={() => !sending && onClose()}>
      <Modal onClick={e => e.stopPropagation()} style={{ maxWidth: '46rem', maxHeight: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column' }}>
        <ModalTitle><Eye size={18} color="#168fc5" />
          {invites.length === 1
            ? <FormattedMessage id="inviteReminders.send.titleOne" defaultMessage="Send an invite reminder to {name}?" values={{ name: current.name }} />
            : <FormattedMessage id="inviteReminders.send.titleMany" defaultMessage="Send invite reminders to {count} speakers?" values={{ count: invites.length }} />}
        </ModalTitle>

        {invites.length > 1 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.82rem', fontWeight: 600, color: '#334155', marginBottom: '.6rem', flexWrap: 'wrap' }}>
            <FormattedMessage id="inviteReminders.send.previewFor" defaultMessage="Preview for" />
            <Select value={index} onChange={e => setIndex(Number(e.target.value))} style={{ flex: 1, minWidth: '12rem' }}>
              {invites.map((invite, i) => <option key={invite.invitationId} value={i}>{invite.name} — {eventNameFor(intl.locale, invite.eventName, invite.eventNameEN)}</option>)}
            </Select>
          </label>
        )}

        <div style={{ fontSize: '.82rem', color: '#475569', marginBottom: '.6rem', lineHeight: 1.5 }}>
          <div><strong><FormattedMessage id="speakerMgmt.preview.to" defaultMessage="To" />:</strong> {current.email}</div>
          <div><strong><FormattedMessage id="speakerMgmt.preview.subject" defaultMessage="Subject" />:</strong> {preview?.subject || '…'}</div>
          <div>
            <FormattedMessage id="inviteReminders.send.history"
              defaultMessage="Invited {invited} · last email {last} · {count, plural, =0 {no reminders yet} one {# reminder so far} other {# reminders so far}}"
              values={{ invited: formatDay(intl, current.invitedAt), last: formatDay(intl, current.lastSentAt), count: current.reminderCount }} />
          </div>
          <div style={{ color: '#64748b', marginTop: '.2rem' }}>
            <FormattedMessage id="inviteReminders.send.hint" defaultMessage="This is exactly what will be sent, using the saved template. Links are switched off in this preview." />
          </div>
        </div>

        {atLimit.length > 0 && (
          <Message warn>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '.3rem' }} />
            <FormattedMessage id="inviteReminders.send.atLimit"
              defaultMessage="{names} already had {max} or more reminders. Automatic reminders have stopped; you can still send one by hand."
              values={{ names: atLimit.map(i => i.name).join(', '), max: maxAutomatic }} />
          </Message>
        )}
        {previewError && <Message error>{previewError}</Message>}

        {preview
          ? <iframe title={t('inviteReminders.preview.title', 'Preview of the reminder')} sandbox="" srcDoc={`<base target="_blank">${preview.html}`}
              style={{ flex: '1 1 auto', width: '100%', minHeight: '50vh', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }} />
          : !previewError && <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}><FormattedMessage id="speakerMgmt.preview.loading" defaultMessage="Preparing preview…" /></div>}

        <ModalActions style={{ marginTop: '1rem' }}>
          <Button secondary disabled={sending} onClick={onClose}><FormattedMessage id="speakerMgmt.confirm.cancel" defaultMessage="Cancel" /></Button>
          <Button disabled={sending || !preview} onClick={send}><Send size={16} />
            {sending
              ? <FormattedMessage id="inviteReminders.send.sending" defaultMessage="Sending…" />
              : <FormattedMessage id="inviteReminders.send.button" defaultMessage="{count, plural, one {Send reminder} other {Send # reminders}}" values={{ count: invites.length }} />}
          </Button>
        </ModalActions>
      </Modal>
    </Overlay>
  );
};

export default ReminderSendModal;
